/**
 * Stage Scorecard Bindings API Schemas (Phase B, B-6)
 *
 * Zod validation schemas for the stage-bindings authoring API and the CRM
 * stages reference endpoint. See StageScorecardBindingRepository
 * (@aica/database) for the row shapes this mirrors, and migration
 * 061_stage_scorecard_bindings.sql for the CHECK-constraint rationale.
 *
 * Endpoints:
 * - GET    /api/v1/stage-bindings     - List org's bindings
 * - POST   /api/v1/stage-bindings     - Create binding (exact | semantic)
 * - PATCH  /api/v1/stage-bindings/:id - Rebind to a different prompt profile
 * - DELETE /api/v1/stage-bindings/:id - Delete binding
 * - GET    /api/v1/crm/stages         - Org's deal pipelines/stages reference
 * - PUT    /api/v1/crm/stage-semantics - Set or clear the customer's phase of stages (#1206)
 */

import { z } from 'zod/v4';
import { crmStageSemanticOriginSchema, crmStageSemanticSchema } from './records';

// ============================================================================
// Create Request — discriminated union mirroring
// StageScorecardBindingRepository.CreateStageBindingInput exactly (same
// `kind` discriminator, same field split).
// ============================================================================

const createStageBindingExactSchema = z
  .object({
    kind: z.literal('exact'),
    crmPipelineId: z.string().min(1, 'crmPipelineId is required'),
    crmStageId: z.string().min(1, 'crmStageId is required'),
    promptProfileId: z.string().min(1, 'promptProfileId is required'),
  })
  .strict();

const createStageBindingSemanticSchema = z
  .object({
    kind: z.literal('semantic'),
    stageSemantic: crmStageSemanticSchema,
    promptProfileId: z.string().min(1, 'promptProfileId is required'),
  })
  .strict();

/**
 * `.strict()` on each branch rejects a body that mixes exact + semantic
 * fields (extra key -> 400); the discriminator itself rejects a body with
 * neither a valid `kind` nor a matching field set (-> 400). This is the
 * schema-level "exactly one of exact/semantic" guarantee.
 */
export const createStageBindingRequestSchema = z.discriminatedUnion('kind', [
  createStageBindingExactSchema,
  createStageBindingSemanticSchema,
]);

// ============================================================================
// Update Request — promptProfileId only. Re-keying which stage/semantic a
// binding covers is delete + create by repository design (see
// StageScorecardBindingRepository.update doc comment) — not exposed here.
// ============================================================================

export const updateStageBindingRequestSchema = z
  .object({
    promptProfileId: z.string().min(1, 'promptProfileId is required'),
  })
  .strict();

// ============================================================================
// Response — binding item
// ============================================================================

export const stageScorecardBindingSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  crmPipelineId: z.string().nullable(),
  crmStageId: z.string().nullable(),
  stageSemantic: crmStageSemanticSchema.nullable(),
  promptProfileId: z.string(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  createdByUserId: z.string().nullable(),
});

export const listStageBindingsResponseSchema = z.object({
  data: z.array(stageScorecardBindingSchema),
  meta: z
    .object({
      total: z.number().int(),
    })
    .optional(),
});

export const stageBindingResponseSchema = z.object({
  data: stageScorecardBindingSchema,
});

// ============================================================================
// Stages Reference — GET /api/v1/crm/stages
// ============================================================================

/**
 * One stage in the reference list. `suggestedSemantic` is the phase stage
 * capture stores for a call at this stage (#1206, `resolveStageSemantic`,
 * @aica/core): Bitrix24's own won/lost marker, else the customer's mapping,
 * else the phase an AI model guessed from the stage names. Scorecard
 * selection reads the explicit bindings in stage_scorecard_bindings.
 */
export const stageReferenceItemSchema = z.object({
  stageId: z.string(),
  name: z.string(),
  suggestedSemantic: crmStageSemanticSchema.nullable(),
  /**
   * `crm` — Bitrix24's own won/lost marker; `customer` — the customer's
   * mapping; `name` — guessed by AI from the stage names. Null with no phase
   * (or from an API older than #1206).
   */
  suggestedSemanticOrigin: crmStageSemanticOriginSchema.nullable().default(null),
  /**
   * The phase AI guessed from the stage names — what clearing the customer's
   * mapping falls back to. Null when the names do not say, or there is no
   * guess yet (see `nameGuessPending`; or from an API older than #1206).
   */
  nameGuessSemantic: crmStageSemanticSchema.nullable().default(null),
  /**
   * true — no guess is cached for this stage yet, and one will be made (the
   * organization has stage-based selection on): the stage is mapped by the
   * customer and was never asked, or the classifier did not answer this time.
   * The tab then says "AI will guess" rather than "no phase".
   */
  nameGuessPending: z.boolean().optional(),
});

export const pipelineStagesReferenceSchema = z.object({
  pipelineId: z.string(),
  name: z.string(),
  stages: z.array(stageReferenceItemSchema),
});

/**
 * `GET /crm/stages` answers 409 `{ code }` with this when a CRM IS connected
 * but the customer paused or withdrew processing of it (Uspacy): the pipelines
 * are not read, which is neither "no CRM" (`{ data: [] }`) nor an outage (503).
 * Additive: a client that does not know the code sees an ordinary error.
 */
export const CRM_PROCESSING_INACTIVE_CODE = 'crm_processing_inactive';

/**
 * The CRM installation the pipelines were read from (#2118): the org's
 * reference install; `null` when no pipelines could be listed as a CRM's (none
 * connected, or one whose provider or credentials give no pipeline reference).
 * The deal stages tab reads and writes the pipelines' sales methodology for it,
 * so a tab that still shows another CRM's pipelines is refused instead of
 * landing on the same-id pipeline of the CRM connected since. Optional: an
 * older server does not name it.
 */
export const crmInstallationIdSchema = z.string().nullable().optional();

/** A CRM pipeline or stage id: the CRM's own string, length-bounded. */
export const crmReferenceIdSchema = z.string().trim().min(1).max(128);

/**
 * The query of a write the deal stages tab makes for its pipelines' ids — the
 * sales methodology (#2118), a stage's phase and an exact stage binding
 * (#2139): `installationId` — the CRM installation whose pipelines the tab
 * shows (`installationId` of `GET /crm/stages`). Pipeline and stage ids are
 * the CRM's own, so the same `1`/`X` is a Uspacy stage and a Bitrix24 one;
 * when the org's reference install is no longer this one, the request is
 * refused with 409 {@link CRM_INSTALLATION_CHANGED_CODE}, nothing is written,
 * and the tab reads the pipelines again. Optional: without it (a client older
 * than the field) the request goes ahead as before. A query, not a body field:
 * the bodies are `.strict()`, and an edge-api rolled back under a newer client
 * would refuse a new field with 400.
 */
export const crmInstallationQuerySchema = z.object({
  installationId: crmReferenceIdSchema.optional(),
});

/** 409 `code` of a request whose `installationId` is not the org's reference install now. */
export const CRM_INSTALLATION_CHANGED_CODE = 'crm_installation_changed';

export const crmStagesReferenceResponseSchema = z.object({
  data: z.array(pipelineStagesReferenceSchema),
  /**
   * The CRM (vendor id) whose pipelines these are — pipeline and stage ids are
   * portal-local, so a screen showing stages of several CRMs names only its
   * own (#2086). Absent with no CRM connected; optional for an older server.
   */
  provider: z.string().optional(),
  installationId: crmInstallationIdSchema,
});

/**
 * `GET /crm/stage-names` — the names of the stages of EVERY active CRM of the
 * organization, one entry per CRM (#2135). `GET /crm/stages` describes one
 * install, the first in the registry's order, so a screen whose rows come from
 * two CRMs (Bitrix24 and Uspacy) named the stages of one and left the other as
 * ids. Pipeline and stage ids are portal-local: a screen looks a row's name up
 * in the entry of the row's own CRM (`provider`). A CRM that is paused, did
 * not answer or has no pipeline reference has no entry — its rows keep their ids.
 */
export const crmStageNamesReferenceSchema = z.object({
  /** The CRM (vendor id) these pipelines come from. */
  provider: z.string(),
  installationId: z.string(),
  pipelines: z.array(
    z.object({
      pipelineId: z.string(),
      name: z.string(),
      stages: z.array(z.object({ stageId: z.string(), name: z.string() })),
    })
  ),
});

export const crmStageNamesResponseSchema = z.object({
  references: z.array(crmStageNamesReferenceSchema),
  /**
   * CRMs (vendor ids) that should have answered and did not — a failed read, an
   * install that would not open. Not the one the customer paused, not one
   * without a pipeline reference. A card shows its names as incomplete.
   */
  unavailable: z.array(z.string()),
});

// ============================================================================
// The customer's phase of a stage (#1206) — PUT /api/v1/crm/stage-semantics
// ============================================================================

/** Bitrix24 ids are short ("C3:EXECUTING"); the cap only bounds the input. */
const crmIdSchema = z.string().min(1).max(128);

/**
 * Stages per PUT. Each stage costs a few D1 statements (mapping write, scan,
 * rewrite portions) in one Worker invocation, which caps its queries — 50
 * stays far below that. Callers with more stages send several requests.
 */
export const STAGE_SEMANTICS_PUT_MAX = 50;

/**
 * `semantic: null` clears the mapping — the stage falls back to the phase AI
 * guessed from the stage names. Calls already recorded at the stage get the new phase unless the
 * CRM's own won/lost marker set it.
 */
export const putStageSemanticsRequestSchema = z
  .object({
    items: z
      .array(
        z
          .object({
            crmPipelineId: crmIdSchema,
            crmStageId: crmIdSchema,
            semantic: crmStageSemanticSchema.nullable(),
          })
          .strict()
      )
      .min(1)
      .max(STAGE_SEMANTICS_PUT_MAX),
  })
  .strict();

export const putStageSemanticsResponseSchema = z.object({
  data: z.object({
    /** Recorded calls whose phase changed. */
    rewritten: z.number().int().nonnegative(),
  }),
});

// ============================================================================
// One-time pass over calls recorded before phases existed (#1206, part 3) —
// platform owner, admin console
// ============================================================================

/**
 * An organization with calls at a deal stage but no phase — in D1 (`calls`,
 * at `stages` stages) or in its Postgres copy (`postgresCalls`), which the
 * mirror never repairs once it falls behind (the rewrite keeps `updated_at`).
 */
export const stagePhaseBackfillOrgSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  calls: z.number().int().nonnegative(),
  stages: z.number().int().nonnegative(),
  postgresCalls: z.number().int().nonnegative(),
  /**
   * Calls whose phase was guessed from the stage name (`origin = 'name'`) and
   * that the stage's current cached AI guess contradicts — the retired word
   * list, an older guess — at stages without a customer mapping: what the
   * pass and the scheduled catch-up can change. Counted only for an org with
   * stage-based selection on.
   * Defaults to 0: a console newer than the edge-api it reads still parses.
   */
  rephaseCalls: z.number().int().nonnegative().default(0),
});

export const stagePhaseBackfillListResponseSchema = z.object({
  data: z.array(stagePhaseBackfillOrgSchema),
  /**
   * false — Postgres did not answer: the list is D1's alone and every
   * `postgresCalls` is 0 for want of a count, not because none are left.
   */
  postgresAvailable: z.boolean(),
});

/**
 * One stage of the pass. `skipped`: `not_in_crm` — the CRM no longer has the
 * stage and the customer never mapped it, there is nothing to go by;
 * `no_phase` — nothing to derive a phase from (no CRM marker, no mapping, no
 * guess); `failed` — its rewrite failed this run (a repeat retries it);
 * `partial` — the run's budget ran out inside it (the next run continues).
 * Either way some of its calls stay without a phase for now.
 */
export const stagePhaseBackfillStageSchema = z.object({
  pipelineId: z.string(),
  stageId: z.string(),
  stageName: z.string().nullable(),
  /** Calls at the stage without a phase before the pass — D1, then Postgres. */
  calls: z.number().int().nonnegative(),
  postgresCalls: z.number().int().nonnegative(),
  /** The phase the stage's calls settled on (the mapping as last read). */
  semantic: crmStageSemanticSchema.nullable(),
  origin: crmStageSemanticOriginSchema.nullable(),
  /**
   * Rows rewritten in each store. Besides the calls without a phase, this
   * brings the stage's derived phases (from the name or the mapping) in line
   * with what capture would give now — so it can exceed `calls`.
   */
  rewritten: z.number().int().nonnegative(),
  postgresRewritten: z.number().int().nonnegative(),
  skipped: z.enum(['not_in_crm', 'no_phase', 'failed', 'partial']).nullable(),
});

export const stagePhaseBackfillRunResponseSchema = z.object({
  data: z.object({
    stages: z.array(stagePhaseBackfillStageSchema),
    rewritten: z.number().int().nonnegative(),
    postgresRewritten: z.number().int().nonnegative(),
    /** Stages left for the next run: one run does a bounded amount of work. */
    remainingStages: z.number().int().nonnegative(),
    /**
     * Calls still without a phase, recounted in both stores after the run
     * (per stage the larger count — they hold the same calls): at skipped
     * stages, at stages left for the next run, and any the run did not reach.
     */
    leftWithoutPhase: z.number().int().nonnegative(),
  }),
});

// ============================================================================
// AI phase of stages (#1206, docs/plans/2026-10-07-ai-stage-phases.md) —
// internal edge-api → workflows-api contract, service token only:
// POST /api/v1/crm/stage-phases/classify
// ============================================================================

/**
 * How long edge-api waits for the classifier: one model call for the whole
 * org (seconds), once per org until a stage name changes. A screen that waits
 * longer shows the stages without the guess rather than hang. The route's
 * worst case stays below it: a 10 s wait for another classification, a 25 s
 * model deadline, then the cost and cache writes (workflows-api
 * `STAGE_PHASE_ROUTE_WAIT_ROUNDS`, `STAGE_PHASE_CALL_TIMEOUT_MS`).
 */
export const STAGE_PHASE_CLASSIFY_PROXY_DEADLINE_MS = 45_000;

/** A name as the CRM gives it; the classifier clips what it sends to the model. */
const crmNameSchema = z.string().max(2_000);

/**
 * The org's pipelines and stages, as edge-api has just read them from the CRM
 * (one read per request — the same catalog the screen shows and a mapping
 * change is checked against). `semantic` — the CRM's own won/lost marker.
 */
export const stagePhaseClassifyRequestSchema = z
  .object({
    orgId: z.string().min(1).max(128),
    /** The CRM the stages come from (`bitrix24`); the cache is per provider. */
    provider: z
      .string()
      .min(1)
      .max(32)
      .regex(/^[a-z0-9_]+$/),
    traceId: z.string().max(128).optional(),
    /**
     * Wait (bounded) for another classification of the org — the default, for
     * a screen. false — a request in the background: another holder means no
     * answer now; the stage-phase catch-up gives its answer to the calls.
     */
    wait: z.boolean().optional(),
    pipelines: z
      .array(
        z
          .object({
            pipelineId: crmIdSchema,
            name: crmNameSchema,
            stages: z
              .array(
                z
                  .object({
                    stageId: crmIdSchema,
                    name: crmNameSchema,
                    semantic: crmStageSemanticSchema.nullable(),
                  })
                  .strict()
              )
              .max(500),
          })
          .strict()
      )
      .max(200),
  })
  .strict();

const stageRefSchema = z.object({ pipelineId: z.string(), stageId: z.string() });

/**
 * The classifier's answer for the stages of the request.
 *
 * `guesses` — every stage that has an answer, from the cache or the model:
 * its phase, or null (the names do not say, or the stage cannot be classified
 * — no name, past the cap, left out by the model). `missing` — stages with no
 * answer this time (the model failed, timed out, was over the org's limit, or
 * another classification held the lease past the wait); never "null phase".
 * `status`: `complete` — nothing missing; `partial` — something is.
 * `source`: `cache` — no model call; `model` — the model was asked.
 */
export const stagePhaseClassifyResponseSchema = z.object({
  status: z.enum(['complete', 'partial']),
  source: z.enum(['cache', 'model']),
  guesses: z.array(
    stageRefSchema.extend({ semantic: crmStageSemanticSchema.nullable() })
  ),
  missing: z.array(stageRefSchema),
});

// ============================================================================
// The model's answer to the stage-phase prompt (workflows-api,
// workers/stage-phases/prompt.ts) — validated item by item
// ============================================================================

/**
 * The phases the model may give an intermediate stage. Not `other`: it has no
 * meaning of its own for an intermediate stage, and Bitrix24 already marks
 * lost stages.
 */
export const STAGE_PHASE_GUESS_SEMANTICS = [
  'discovery',
  'qualification',
  'proposal',
  'negotiation',
  'closing',
  'post_sale',
] as const;

/** The answer's envelope; its items are checked one by one. */
export const stagePhaseModelAnswerSchema = z.object({ stages: z.array(z.unknown()) });

/** One answered stage: our key and a phase from the list, or null. */
export const stagePhaseModelAnswerItemSchema = z.object({
  key: z.string(),
  phase: z.enum(STAGE_PHASE_GUESS_SEMANTICS).nullable(),
});

// ============================================================================
// Type exports
// ============================================================================

export type CreateStageBindingRequest = z.infer<typeof createStageBindingRequestSchema>;
export type UpdateStageBindingRequest = z.infer<typeof updateStageBindingRequestSchema>;
export type StageScorecardBindingResponseItem = z.infer<
  typeof stageScorecardBindingSchema
>;
export type ListStageBindingsResponse = z.infer<typeof listStageBindingsResponseSchema>;
export type StageBindingResponse = z.infer<typeof stageBindingResponseSchema>;
export type StageReferenceItem = z.infer<typeof stageReferenceItemSchema>;
export type PipelineStagesReference = z.infer<typeof pipelineStagesReferenceSchema>;
export type CrmStagesReferenceResponse = z.infer<typeof crmStagesReferenceResponseSchema>;
export type CrmStageNamesReference = z.infer<typeof crmStageNamesReferenceSchema>;
export type CrmStageNamesResponse = z.infer<typeof crmStageNamesResponseSchema>;
export type PutStageSemanticsRequest = z.infer<typeof putStageSemanticsRequestSchema>;
export type PutStageSemanticsResponse = z.infer<typeof putStageSemanticsResponseSchema>;
export type StagePhaseBackfillOrg = z.infer<typeof stagePhaseBackfillOrgSchema>;
export type StagePhaseBackfillStage = z.infer<typeof stagePhaseBackfillStageSchema>;
export type StagePhaseBackfillRunResponse = z.infer<
  typeof stagePhaseBackfillRunResponseSchema
>;
export type StagePhaseClassifyRequest = z.infer<typeof stagePhaseClassifyRequestSchema>;
export type StagePhaseClassifyResponse = z.infer<typeof stagePhaseClassifyResponseSchema>;
